import { useEffect, useState, type ReactNode } from "react";
import MovieCard from "@/components/feature/MovieCard";
import type { Movie } from "@/types/content";

interface FilterPanelProps {
  keyword: string;
  genre: string;
  year: string;
  rating: string;
  sort: string;
  resultCount: number;
  genres: string[];
  yearOptions: { value: string; label: string }[];
  ratingOptions: { value: string; label: string }[];
  onKeywordChange: (value: string) => void;
  onChange: (key: "genre" | "year" | "rating" | "sort", value: string) => void;
  onReset: () => void;
  extraContent?: ReactNode;
}

function Pill({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
        active
          ? "bg-accent-500 text-background-950"
          : "border border-background-700 text-foreground-600 hover:border-accent-500 hover:text-accent-400"
      }`}
    >
      {children}
    </button>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-start sm:gap-5">
      <p className="w-16 shrink-0 pt-1 font-label text-xs uppercase tracking-[0.18em] text-foreground-500">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({
  keyword,
  genre,
  year,
  rating,
  sort,
  resultCount,
  genres,
  yearOptions,
  ratingOptions,
  onKeywordChange,
  onChange,
  onReset,
  extraContent,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const hasFilters = Boolean(keyword || genre || year || rating);

  return (
    <div className="rounded-lg border border-background-800 bg-background-900">
      <div className="flex flex-col gap-3 border-b border-background-800 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <form
          className="flex w-full items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3 md:max-w-md"
          onSubmit={(event) => {
            event.preventDefault();
            onKeywordChange(input.trim());
          }}
        >
          <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            name="category_search"
            placeholder="搜索片名、导演、演员"
            className="h-10 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
          {input && (
            <button
              type="button"
              onClick={() => {
                setInput("");
                onKeywordChange("");
              }}
              className="text-foreground-500 hover:text-foreground-900"
              aria-label="清空搜索"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
            </button>
          )}
          <button
            type="submit"
            className="shrink-0 border-l border-background-700 py-1.5 pl-2 text-xs text-accent-400 hover:text-accent-300"
          >
            搜索
          </button>
        </form>

        <div className="flex items-center justify-between gap-3 md:justify-end">
          <p className="text-xs text-foreground-500">
            共 <span className="text-foreground-900">{resultCount}</span> 部作品
          </p>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center gap-1.5 rounded-md border border-background-700 px-3 py-2 text-xs text-foreground-600 md:hidden"
          >
            <i className="ri-filter-3-line" aria-hidden="true"></i>
            筛选条件
            <i
              className={`ri-arrow-down-s-line transition-transform ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} p-4 md:block md:p-5`}>
        <div className="space-y-4">
          <FilterGroup label="题材">
            <Pill active={!genre} onClick={() => onChange("genre", "")}>
              全部
            </Pill>
            {genres.map((item) => (
              <Pill
                key={item}
                active={genre === item}
                onClick={() => onChange("genre", genre === item ? "" : item)}
              >
                {item}
              </Pill>
            ))}
          </FilterGroup>

          <FilterGroup label="年代">
            <Pill active={!year} onClick={() => onChange("year", "")}>
              全部
            </Pill>
            {yearOptions.map((item) => (
              <Pill
                key={item.value}
                active={year === item.value}
                onClick={() => onChange("year", year === item.value ? "" : item.value)}
              >
                {item.label}
              </Pill>
            ))}
          </FilterGroup>

          <FilterGroup label="评分">
            <Pill active={!rating} onClick={() => onChange("rating", "")}>
              全部
            </Pill>
            {ratingOptions.map((item) => (
              <Pill
                key={item.value}
                active={rating === item.value}
                onClick={() => onChange("rating", rating === item.value ? "" : item.value)}
              >
                {item.label}
              </Pill>
            ))}
          </FilterGroup>

          <div className="flex flex-col gap-3 border-t border-background-800 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <FilterGroup label="排序">
              <div className="flex rounded-full border border-background-700 p-1">
                {[
                  { value: "updated", label: "最近更新" },
                  { value: "rating", label: "评分最高" },
                  { value: "year", label: "年份最新" },
                ].map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => onChange("sort", item.value)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                      sort === item.value
                        ? "bg-secondary-700 text-foreground-950"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </FilterGroup>
            {hasFilters && (
              <button
                type="button"
                onClick={onReset}
                className="flex w-fit items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-accent-400"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置全部筛选
              </button>
            )}
          </div>
        </div>
        {extraContent}
      </div>
    </div>
  );
}

export function MovieGrid({ items }: { items: Movie[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-5 lg:grid-cols-4">
      {items.map((movie) => (
        <MovieCard key={movie.id} movie={movie} />
      ))}
    </div>
  );
}